"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";
import { AIAssistantModal } from "@/components/ui/AIAssistantModal";
import { formatCurrency } from "@/lib/utils";

type RepairStatus =
  | "RECEIVED"
  | "DIAGNOSED"
  | "QUOTED"
  | "APPROVED"
  | "IN_SHOP"
  | "AT_VENDOR"
  | "COMPLETED"
  | "READY_FOR_PICKUP"
  | "PICKED_UP"
  | "CANCELLED";

interface Repair {
  id: string;
  repairNumber: string;
  status: RepairStatus;
  itemDescription: string;
  itemPhotoUrls: string[];
  itemMetalType: string | null;
  requestedWork: string;
  diagnosis: string | null;
  workToBePerformed: string | null;
  quotedPrice: number | null;
  actualPrice: number | null;
  rushFee: number | null;
  depositAmount: number | null;
  droppedOffDate: string;
  promisedDate: string | null;
  completedDate: string | null;
  sentToVendor: boolean;
  vendor: { id: string; name: string } | null;
  customer: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    photoUrl: string | null;
    email: string | null;
    phone: string | null;
    isVip: boolean;
  };
  notes: string | null;
}

const STATUS_META: Record<RepairStatus, { label: string; color: string }> = {
  RECEIVED: { label: "Received", color: "bg-white/10 text-white/80 border-white/20" },
  DIAGNOSED: { label: "Diagnosed", color: "bg-white/10 text-white/80 border-white/20" },
  QUOTED: { label: "Quoted", color: "bg-amber-200/10 text-amber-200 border-amber-200/30" },
  APPROVED: { label: "Approved", color: "bg-violet-300/10 text-violet-200 border-violet-300/30" },
  IN_SHOP: { label: "In shop", color: "bg-sky-300/10 text-sky-200 border-sky-300/30" },
  AT_VENDOR: { label: "At vendor", color: "bg-orange-300/10 text-orange-200 border-orange-300/30" },
  COMPLETED: { label: "Completed", color: "bg-emerald-300/10 text-emerald-200 border-emerald-300/30" },
  READY_FOR_PICKUP: { label: "Ready for pickup", color: "bg-emerald-300/10 text-emerald-200 border-emerald-300/30" },
  PICKED_UP: { label: "Picked up", color: "bg-white/5 text-white/60 border-white/10" },
  CANCELLED: { label: "Cancelled", color: "bg-white/5 text-white/60 border-white/10" },
};

const FILTERS: Array<{ value: "ALL" | "OPEN" | RepairStatus; label: string }> = [
  { value: "ALL", label: "All" },
  { value: "OPEN", label: "Open" },
  { value: "READY_FOR_PICKUP", label: "Ready" },
  { value: "IN_SHOP", label: "In shop" },
  { value: "AT_VENDOR", label: "At vendor" },
  { value: "QUOTED", label: "Quoted" },
  { value: "PICKED_UP", label: "Picked up" },
];

function daysUntil(iso: string): number {
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

export default function RepairsPage() {
  const [repairs, setRepairs] = useState<Repair[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"ALL" | "OPEN" | RepairStatus>("ALL");
  const [modal, setModal] = useState<null | { entity: string; example: string }>(null);

  useEffect(() => {
    fetch("/api/repairs")
      .then((r) => r.json())
      .then((d) => setRepairs(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "ALL") return repairs;
    if (filter === "OPEN") return repairs.filter((r) => r.status !== "PICKED_UP" && r.status !== "CANCELLED");
    return repairs.filter((r) => r.status === filter);
  }, [repairs, filter]);

  const stats = useMemo(() => {
    const open = repairs.filter((r) => r.status !== "PICKED_UP" && r.status !== "CANCELLED");
    const ready = repairs.filter((r) => r.status === "READY_FOR_PICKUP" || r.status === "COMPLETED");
    const atVendor = repairs.filter((r) => r.status === "AT_VENDOR");
    const overdue = open.filter((r) => r.promisedDate && daysUntil(r.promisedDate) < 0).length;
    return { open: open.length, ready: ready.length, atVendor: atVendor.length, overdue };
  }, [repairs]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-300/30 to-emerald-500/10">
              <svg className="h-4 w-4 text-emerald-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white">Service & Repairs</h1>
          </div>
          <p className="mt-1 text-sm text-muted">
            Track take-ins, vendor handoffs, and ready-for-pickup jobs.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ entity: "service job", example: "New repair: Emily\u2019s ring resize from 6 to 6.5, promise date next Friday." })}
          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/90"
        >
          + New service job
        </button>
      </div>

      <AIAssistantModal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        entity={modal?.entity || "service job"}
        example={modal?.example}
      />

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Open jobs</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.open}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Ready for pickup</p>
          <p className="mt-1 text-xl font-semibold text-emerald-200">{stats.ready}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">At vendor</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.atVendor}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Overdue</p>
          <p className="mt-1 text-xl font-semibold text-amber-200">{stats.overdue}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition ${
                active
                  ? "border-emerald-300/40 bg-emerald-300/10 text-emerald-200"
                  : "border-white/10 bg-white/5 text-white/60 hover:border-white/20 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No service jobs"
          description="Open repairs, resizings, restringing, and diagnostics will appear here."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status];
            const fullName = [r.customer.firstName, r.customer.lastName].filter(Boolean).join(" ");
            const due = r.promisedDate ? daysUntil(r.promisedDate) : null;
            const price = r.actualPrice ?? r.quotedPrice;
            return (
              <Card key={r.id} className="transition hover:border-white/20">
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {r.itemPhotoUrls?.[0] ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={r.itemPhotoUrls[0]}
                        alt={r.itemDescription}
                        className="h-20 w-20 flex-shrink-0 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-xl bg-white/5 text-white/30">
                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${meta.color}`}>
                          {meta.label}
                        </span>
                        <span className="text-xs font-mono text-text-dim">{r.repairNumber}</span>
                        {due !== null && r.status !== "PICKED_UP" && r.status !== "CANCELLED" && (
                          <Badge variant={due < 0 ? "danger" : due <= 3 ? "warning" : "default"} size="sm">
                            {due < 0 ? `${Math.abs(due)}d overdue` : due === 0 ? "Due today" : `Promised in ${due}d`}
                          </Badge>
                        )}
                        {r.sentToVendor && r.vendor && (
                          <span className="rounded-full border border-orange-300/30 bg-orange-300/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-orange-200">
                            Vendor: {r.vendor.name}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-white">{r.itemDescription}</h3>
                        <p className="text-sm text-white/60">
                          <Link href={`/dashboard/customers/${r.customer.id}`} className="hover:text-white">
                            {fullName}
                          </Link>
                          {r.customer.isVip && (
                            <span className="ml-2 rounded-full border border-amber-200/30 bg-amber-200/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-200">
                              VIP
                            </span>
                          )}
                        </p>
                      </div>
                      <p className="text-xs text-white/70">{r.workToBePerformed || r.requestedWork}</p>
                      {r.diagnosis && (
                        <p className="text-[11px] text-text-dim italic">Diagnosis: {r.diagnosis}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 text-right">
                      {price ? (
                        <>
                          <span className="text-xs uppercase tracking-wider text-text-dim">
                            {r.actualPrice ? "Price" : "Quoted"}
                          </span>
                          <span className="text-lg font-semibold text-white">{formatCurrency(price)}</span>
                        </>
                      ) : (
                        <span className="text-xs text-text-dim">Pending quote</span>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          const isReady = r.status === "READY_FOR_PICKUP" || r.status === "COMPLETED";
                          setModal({
                            entity: isReady ? "pickup notification" : "status update",
                            example: isReady
                              ? `Text ${r.customer?.firstName || "the client"} that ${r.repairNumber} is ready for pickup.`
                              : `Move ${r.repairNumber} to next status and note the update.`,
                          });
                        }}
                        className="mt-2 rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-white/20"
                      >
                        {r.status === "READY_FOR_PICKUP" || r.status === "COMPLETED"
                          ? "Notify client"
                          : "Update status"}
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
